<template>
  <div class="md-root">
    <!-- Sleek Header -->
    <div class="md-header">
      <div class="d-flex align-center" style="gap: 12px">
        <v-btn icon dark small @click="$emit('back')" class="md-back-btn">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="text-subtitle-1 font-weight-bold white--text">{{ monthName }}</div>
          <div class="text-caption" style="color: rgba(255, 255, 255, 0.35)">Einsatzplan bearbeiten</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap: 8px">
        <v-btn small depressed color="indigo" class="md-action-btn font-weight-bold" @click="editMode = !editMode">
          <v-icon left x-small>{{ editMode ? "mdi-calendar-month" : "mdi-auto-fix" }}</v-icon>
          {{ editMode ? "Roster-Ansicht" : "Assistenten-Modus" }}
        </v-btn>
        <v-btn small depressed dark class="md-action-btn" @click="confirmRecalculateDialog = true" :loading="recalculating" v-if="!editMode">
          <v-icon left x-small>mdi-calculator-variant</v-icon> Neu berechnen
        </v-btn>
        <v-btn small depressed color="error" class="md-action-btn" @click="confirmDeleteDialog = true" :loading="deleting" v-if="!editMode">
          <v-icon left x-small>mdi-delete-sweep</v-icon> Plan leeren
        </v-btn>
        <v-btn small depressed class="md-refresh-btn" @click="refreshData" :loading="loading || recalculating || deleting">
          <v-icon left x-small>mdi-refresh</v-icon> Aktualisieren
        </v-btn>
      </div>
    </div>

    <!-- Content -->
    <div class="md-content">
      <shift-management-assistant
        v-if="!loading && editMode"
        :studio-id="studioId"
        :start-date="startDate"
        :end-date="endDate"
        :available-shifts="settings.shifts"
        :school-holidays="settings.schoolHolidays || []"
        :public-holidays="publicHolidays"
        :employees="assignedEmployees"
        @back="editMode = false"
        @view-plan="
          editMode = false
          refreshData()
        " />
      <shift-management-roster-view
        v-else-if="!loading && !editMode && (assignedPlanData.length > 0 || unassignedShifts.length > 0) && assignedEmployees.length > 0"
        :plan-data="assignedPlanData"
        :studioId="studioId"
        :availableShifts="settings.shifts || []"
        :unassignedShifts="unassignedShifts"
        :employees="assignedEmployees"
        :available-employees="availableEmployeesToAdd"
        :start-date="startDate"
        :school-holidays="settings.schoolHolidays || []"
        :public-holidays="publicHolidays"
        :previous-context="previousContext"
        :loading="loading"
        @add-employee="onAddEmployee"
        @refresh="refreshData" />
      <div v-else-if="loading" class="md-loading">
        <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
        <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Planungsdaten…</p>
      </div>
      <div v-else class="md-loading">
        <v-icon size="48" color="rgba(255,255,255,0.15)">mdi-calendar-blank-outline</v-icon>
        <p class="mt-3" style="color: rgba(255, 255, 255, 0.4)">Keine Zuweisungen gefunden.</p>
        <v-btn small depressed color="green accent-3" class="mt-3 black--text" @click="$emit('back')">Zurück</v-btn>
      </div>
    </div>

    <!-- Recalc Dialog -->
    <v-dialog v-model="confirmRecalculateDialog" max-width="480px" dark>
      <v-card dark class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="amber">mdi-alert-circle</v-icon>
          <span style="font-size: 1rem">Monat neu berechnen?</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          <ol class="mt-1 mb-2" style="padding-left: 16px">
            <li>Alle <b>Standard-Schichten</b> werden geleert.</li>
            <li>Der Auto-Assigner startet <b>sofort neu</b>.</li>
          </ol>
          <div class="text-caption mt-2" style="color: rgba(255, 255, 255, 0.5)">Spezial-/Fix-Schichten bleiben erhalten.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmRecalculateDialog = false" :disabled="recalculating">Abbrechen</v-btn>
          <v-btn color="amber" text @click="executeRecalculation" :loading="recalculating" class="font-weight-bold">Ja, berechnen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Delete Dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="480px" dark>
      <v-card dark class="rounded-xl">
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="red">mdi-delete-sweep</v-icon>
          <span style="font-size: 1rem">Plan komplett leeren?</span>
        </v-card-title>
        <v-card-text class="text-body-2">
          Möchtest du wirklich alle Schichten in diesem Monat löschen? Das kann nicht rückgängig gemacht werden.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDeleteDialog = false" :disabled="deleting">Abbrechen</v-btn>
          <v-btn color="red" text @click="executeDelete" :loading="deleting" class="font-weight-bold">Ja, leeren</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import ShiftManagementRosterView from "./shiftManagementRosterView.vue"
import ShiftManagementAssistant from "./shiftManagementAssistant.vue"

export default {
  name: "ShiftManagementMonthDetail",
  components: {
    ShiftManagementRosterView,
    ShiftManagementAssistant,
  },
  props: {
    studioId: {
      type: String,
      required: true,
    },
    monthId: {
      type: String, // Format: "YYYY-MM"
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      settings: {},
      allEmployees: [],
      assignedPlanData: [],
      unassignedShifts: [],
      assignedEmployees: [],
      publicHolidays: [],
      previousContext: null,
      confirmRecalculateDialog: false,
      recalculating: false,
      confirmDeleteDialog: false,
      deleting: false,
      manuallyAddedEmployeeIds: [],
      availableEmployeesToAdd: [],
      editMode: false,
    }
  },
  computed: {
    monthName() {
      return this.$moment(this.monthId, "YYYY-MM").format("MMMM YYYY")
    },
    startDate() {
      return this.$moment(this.monthId, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
    },
    endDate() {
      return this.$moment(this.monthId, "YYYY-MM").endOf("month").format("YYYY-MM-DD")
    },
  },
  async created() {
    await this.initData()
  },
  watch: {
    monthId() {
      this.editMode = false
      this.initData()
    },
  },
  methods: {
    async initData() {
      this.loading = true
      try {
        const [settingsRes, employeesRes, publicHolidaysRes, previousContextRes] = await Promise.all([
          api.shiftManagement.fetchSettings("local"),
          api.shiftManagement.fetchShiftManagementEmployee(),
          api.shiftManagement.fetchPublicHolidays(this.studioId, this.startDate, this.endDate),
          api.shiftManagement.fetchPreviousMonthContext(this.studioId, this.startDate),
        ])
        this.settings = settingsRes
        this.allEmployees = employeesRes
        this.publicHolidays = publicHolidaysRes || []
        this.previousContext = previousContextRes || null
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Initialisieren des Monats", err)
        this.$toast.error("Konnte Basisdaten nicht laden.")
      } finally {
        this.loading = false
      }
    },
    async refreshData() {
      this.loading = true
      try {
        const response = await api.shiftManagement.fetchPlan(this.studioId, this.startDate, this.endDate)
        const responseData = response && response.data ? response.data : response
        const freshShifts = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []

        this.assignedPlanData = freshShifts.filter((p) => p.assignedEmployee)
        this.unassignedShifts = freshShifts.filter((p) => !p.assignedEmployee)

        this.prepareEmployees()
      } catch (err) {
        console.error("Fehler beim Laden der Schichtdaten", err)
        this.$toast.error("Konnte Schichtplan nicht laden.")
      } finally {
        this.loading = false
      }
    },
    async executeRecalculation() {
      if (!this.studioId || !this.startDate || !this.endDate) return

      this.recalculating = true
      try {
        // 1. Reset
        await api.shiftManagement.resetPlanAssignments(this.studioId, this.startDate, this.endDate)

        // 2. Erstelle Setting-Objekt für Assign-Route
        const planningSettings = {}
        const employeeIds = []

        this.assignedEmployees.forEach((emp) => {
          employeeIds.push(emp._id)
          planningSettings[emp._id] = emp.planningSettings
        })

        if (employeeIds.length === 0) {
          this.$toast.warning("Keine Mitarbeiter für die Zuweisung gefunden.")
          this.confirmRecalculateDialog = false
          return
        }

        // 3. Neu Zuweisen
        await api.shiftManagement.assignEmployeesToPlan(this.studioId, this.startDate, this.endDate, planningSettings, employeeIds)

        this.$toast.success("Einsatzplan erfolgreich neu berechnet!")
        this.confirmRecalculateDialog = false

        // 4. Frische Daten laden
        await this.refreshData()
      } catch (err) {
        console.error("Fehler bei Neuberechnung:", err)
        this.$toast.error("Fehler bei der Neuberechnung.")
      } finally {
        this.recalculating = false
      }
    },
    async executeDelete() {
      if (!this.assignedPlanData.length && !this.unassignedShifts.length) {
        this.confirmDeleteDialog = false
        return
      }
      this.deleting = true
      try {
        const allShifts = [...this.assignedPlanData, ...this.unassignedShifts]
        const promises = allShifts.map((s) => api.shiftManagement.deleteShift(s._id || s))
        await Promise.all(promises)
        this.$toast.success("Plan erfolgreich geleert.")
        this.confirmDeleteDialog = false
        await this.refreshData()
      } catch (err) {
        console.error("Fehler beim Löschen:", err)
        this.$toast.error("Fehler beim Löschen des Plans.")
      } finally {
        this.deleting = false
      }
    },
    prepareEmployees() {
      let assignedUserIds = [...new Set(this.assignedPlanData.map((p) => String(p.assignedEmployee._id || p.assignedEmployee)))]

      const sourceEmployees = this.allEmployees.filter((emp) => emp.studio && emp.studio.some((s) => String(s._id) === String(this.studioId)))

      sourceEmployees.forEach((e) => {
        const uId = String(e.user._id || e.user)
        const backendSettings = (e.shiftPlanningSettings && e.shiftPlanningSettings[this.studioId]) || {}
        const settingsKey = `planningSettings_${e._id}_${this.studioId}`
        const localSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
        const savedSettings = Object.keys(backendSettings).length > 0 ? backendSettings : localSettings

        // Füge MA hinzu, die auf "Bereit" stehen ODER die wir manuell über den Button hinzugefügt haben
        if ((savedSettings.isReadyForPlanning || this.manuallyAddedEmployeeIds.includes(uId)) && !assignedUserIds.includes(uId)) {
          assignedUserIds.push(uId)
        }
      })

      // Generiere die Liste der eingeblendeten Mitarbeiter
      this.assignedEmployees = assignedUserIds
        .map((userId) => {
          const employeeData = sourceEmployees.find((e) => String(e.user._id || e.user) === userId)
          if (!employeeData) return null

          const backendSettings = (employeeData.shiftPlanningSettings && employeeData.shiftPlanningSettings[this.studioId]) || {}
          const settingsKey = `planningSettings_${employeeData._id}_${this.studioId}`
          const localSettings = JSON.parse(localStorage.getItem(settingsKey)) || {}
          const savedSettings = Object.keys(backendSettings).length > 0 ? backendSettings : localSettings
          const planningSettings = {
            percentages: savedSettings.percentages || {},
            recurringAbsence: savedSettings.recurringAbsence || [],
            recurringExternalStudio: savedSettings.recurringExternalStudio || [],
            isReadyForPlanning: savedSettings.isReadyForPlanning || false,
          }

          // Filter Ferien und Freiwünsche/Schichtwünsche für diesen Monat
          const rawVacations = (employeeData.holidayEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const dates = e.dates
            if (!dates || !dates.length) return false
            const start = this.$moment(dates[0].date)
            const end = this.$moment(dates[dates.length - 1].date)
            return start.isSameOrBefore(this.endDate) && end.isSameOrAfter(this.startDate)
          })

          const vacations = rawVacations.map((h) => ({
            ...h,
            formattedRange:
              h.dates && h.dates.length
                ? `${this.$moment(h.dates[0].date).format("DD.MM")} - ${this.$moment(h.dates[h.dates.length - 1].date).format("DD.MM")}`
                : "",
          }))

          const freeWishes = (employeeData.freeWishEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const date = this.$moment(e.day)
            return date.isBetween(this.startDate, this.endDate, null, "[]")
          })

          const shiftWishes = (employeeData.shiftWishEntries || []).filter((e) => {
            if (e.status && e.status !== "accepted") return false
            const date = this.$moment(e.day)
            return date.isBetween(this.startDate, this.endDate, null, "[]")
          })

          return {
            ...employeeData,
            _id: userId,
            vacations,
            freeWishes,
            shiftWishes,
            sollStunden: this.calculateSollStunden(employeeData, planningSettings),
            plannedHours:
              this.calculatePlannedHours(userId, this.assignedPlanData) +
              this.calculatePaidVacationHours({ ...employeeData, vacations }, planningSettings),
            planningSettings,
          }
        })
        .filter((e) => e !== null)

      // NEU: Generiere die Liste der "verfügbaren" Mitarbeiter (die noch nicht angezeigt werden)
      this.availableEmployeesToAdd = sourceEmployees
        .filter((e) => !assignedUserIds.includes(String(e.user._id || e.user)))
        .map((e) => ({
          _id: String(e.user._id || e.user),
          fullName: e.fullName,
          picture: e.picture,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
    onAddEmployee(userId) {
      if (!this.manuallyAddedEmployeeIds.includes(userId)) {
        this.manuallyAddedEmployeeIds.push(userId)
        this.prepareEmployees() // Roster neu berechnen
      }
    },
    calculateSollStunden(employeeData, planningSettings) {
      if (!employeeData) return 0
      const start = this.$moment(this.startDate)
      const end = this.$moment(this.endDate)
      let businessDays = 0
      let current = start.clone()

      const studioInfo = employeeData.studio.find((s) => s._id === this.studioId)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)

      // NEU: Feiertage-Set für schnellen Lookup
      const publicHolidayDates = new Set((this.publicHolidays || []).filter((h) => !h.isWeekend).map((h) => h.date))

      while (current.isSameOrBefore(end, "day")) {
        const dayOfWeekISO = current.isoWeekday()
        const isWeekend = dayOfWeekISO >= 6
        const dateStr = current.format("YYYY-MM-DD")
        const isSchoolHoliday = (this.settings.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        // NEU: Feiertage abziehen
        const isPublicHoliday = publicHolidayDates.has(dateStr) && !isWeekend

        let isSchoolDay = false
        if (employeeData.isApprentice && !isSchoolHoliday) {
          isSchoolDay = (employeeData.schoolDays || []).some((sd) => sd.dayOfWeek === dayOfWeekISO)
        }

        let isWorkDay = !isWeekend && !isSchoolDay && !isPublicHoliday
        if (employeeData.isApprentice && isSchoolHoliday) {
          isWorkDay = false
        }

        if (isWorkDay) businessDays++

        // Anderes Studio abziehen (immer, auch am Wochenende!)
        if ((planningSettings.recurringExternalStudio || []).includes(dayOfWeekISO)) {
          businessDays--
        }
        current.add(1, "day")
      }
      return businessDays * (8.4 * (percentage / 100))
    },
    calculatePlannedHours(userId, planData) {
      let totalHours = 0
      planData.forEach((plan) => {
        const planUserId = String(plan.assignedEmployee._id || plan.assignedEmployee)
        if (planUserId === String(userId)) {
          const timesToUse = plan.times && plan.times.length > 0 ? plan.times : plan.shiftTemplate ? plan.shiftTemplate.times : []
          if (timesToUse) {
            timesToUse.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const startTime = this.$moment(time.start, "HH:mm")
                const endTime = this.$moment(time.end, "HH:mm")
                if (endTime.isBefore(startTime)) endTime.add(1, "day")
                totalHours += endTime.diff(startTime, "hours", true)
              }
            })
          }
        }
      })
      return totalHours
    },
    calculatePaidVacationHours(employeeData, planningSettings) {
      const start = this.$moment(this.startDate)
      const end = this.$moment(this.endDate)
      const studioInfo = employeeData.studio.find((s) => s._id === this.studioId)
      if (!studioInfo) return 0

      const percentage = this.getPlanningPercentage(employeeData, studioInfo, planningSettings)
      const dailyHours = 8.4 * (percentage / 100)
      let vacationHours = 0

      const vacations = employeeData.vacations || []
      vacations.forEach((vac) => {
        ;(vac.dates || []).forEach((d) => {
          const dateMoment = this.$moment(d.date)
          if (dateMoment.isBetween(start, end, null, "[]")) {
            if (dateMoment.isoWeekday() <= 5) {
              vacationHours += dailyHours
            }
          }
        })
      })
      return vacationHours
    },
    getPlanningPercentage(employee, studioInfo, planningSettings) {
      if (!studioInfo && !employee.timeTrackerPercentage) return 0
      const planningPercentages = planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null && planningPercentages[studioInfo._id] !== "") {
        return planningPercentages[studioInfo._id]
      }
      if (employee.timeTrackerPercentage != null) return employee.timeTrackerPercentage
      if (studioInfo) return studioInfo.percentage
      return 0
    },
  },
}
</script>

<style scoped>
.md-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #1a1a1a;
}
.md-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.md-back-btn {
  background: rgba(255, 255, 255, 0.06) !important;
}
.md-action-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}
.md-refresh-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}
.md-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.md-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
